import { formatTableData, getData } from '../utils/formatters';

interface EagleMountainData {
  address?: string;
  city?: string;
  zip?: string;
  parcelId?: string;
  parcelSerial?: string;
  ownerName?: string;
  ownerMailingAddress?: string;
  acres?: string | number;
  zone?: string;
  zoningCode?: string;
  zoningLabel?: string;
  generalZoning?: string;
  currentLandUse?: string;
  mdaName?: string;
  mdaLink?: string;
  mdaMapLink?: string;
  municipalCode?: string;
  municipalCodeLink?: string;
  historicCode?: string;
  historicCodeLink?: string;
  ordinanceNumber?: string;
  countyParcelUrl?: string;
}

const linkedValue = (label: string | undefined, url: string | undefined, fallback: string): string => {
  if (label && url) {
    return `[${label}](${url})`;
  }
  if (label) {
    return label;
  }
  if (url) {
    return `[View](${url})`;
  }
  return fallback;
};

const EAGLE_MOUNTAIN_ROWS = [
  {
    label: 'Address',
    getValue: (data: EagleMountainData) => getData(data, 'address', 'No address available')
  },
  {
    label: 'City, Zip',
    getValue: (data: EagleMountainData) =>
      `${getData(data, 'city', 'N/A')}, ${getData(data, 'zip', 'N/A')}`
  },
  {
    label: 'Parcel ID',
    getValue: (data: EagleMountainData) => getData(data, 'parcelId', 'No parcel ID available')
  },
  {
    label: 'Parcel Serial',
    getValue: (data: EagleMountainData) =>
      getData(data, 'parcelSerial', 'No parcel serial available')
  },
  {
    label: 'Owner',
    getValue: (data: EagleMountainData) => getData(data, 'ownerName', 'No owner information available')
  },
  {
    label: 'Owner Mailing Address',
    getValue: (data: EagleMountainData) =>
      getData(data, 'ownerMailingAddress', 'No owner mailing address available')
  },
  {
    label: 'Property Size',
    getValue: (data: EagleMountainData) => {
      const acres = data.acres;
      if (acres === undefined || acres === null || acres === '') {
        return 'No property size information available';
      }
      return `${acres} acres`;
    }
  },
  {
    label: 'Zone',
    getValue: (data: EagleMountainData) => {
      const label = data.zoningLabel || data.zone;
      const code = data.zoningCode;
      if (label) {
        return code && code !== label ? `${label} (${code})` : label;
      }
      if (code) {
        return code;
      }
      return 'No zoning information available';
    }
  },
  {
    label: 'General Zoning',
    getValue: (data: EagleMountainData) =>
      getData(data, 'generalZoning', 'No general zoning available')
  },
  {
    label: 'Current Land Use',
    getValue: (data: EagleMountainData) =>
      getData(data, 'currentLandUse', 'No current land use available')
  },
  {
    label: 'MDA',
    getValue: (data: EagleMountainData) =>
      linkedValue(data.mdaName, data.mdaLink, 'No MDA information available')
  },
  {
    label: 'MDA Map',
    getValue: (data: EagleMountainData) => {
      if (data.mdaMapLink) {
        return `[View MDA Map](${data.mdaMapLink})`;
      }
      return 'No MDA map available';
    }
  },
  {
    label: 'Municipal Code',
    getValue: (data: EagleMountainData) =>
      linkedValue(data.municipalCode, data.municipalCodeLink, 'No municipal code available')
  },
  {
    label: 'Historic Code',
    getValue: (data: EagleMountainData) =>
      linkedValue(data.historicCode, data.historicCodeLink, 'No historic code available')
  },
  {
    label: 'Ordinance',
    getValue: (data: EagleMountainData) =>
      getData(data, 'ordinanceNumber', 'No ordinance number available')
  },
  {
    label: 'County Parcel Record',
    getValue: (data: EagleMountainData) => {
      if (data.countyParcelUrl) {
        return `[View County Parcel Record](${data.countyParcelUrl})`;
      }
      return 'No county parcel record available';
    }
  }
];

export const formatEagleMountainData = (data: EagleMountainData): string => {
  return formatTableData(data, EAGLE_MOUNTAIN_ROWS);
};
